/**
 * HOW TO USE THIS PAGE, at the very top of a bag group (bag group rethink,
 * 2026-10-19, /mnt/project-files/bag-groups/redesign/RETHINK.md).
 *
 * One bar, closed by default, one tap to open. The steps describe the page's
 * own controls and nothing about the bags themselves, so every sentence below
 * is copied word for word from the rethink, which checked each one against
 * BagGroupDetail.tsx, BagGroupBoard.tsx and BagGroupReadOnly.tsx. Change a
 * control there and change its sentence here, in the same change.
 *
 * Who sees what:
 *   - the six family steps, each only alongside the control it describes,
 *     gated on the very flags the page gates that control with (final
 *     review on the rethink): Your family for a member; sign-up and
 *     drop-off for whoever can act on items (canActOnItems: an active
 *     member whose role may join, never a dancer sign-in); Posts for whoever
 *     can read them (canReadChat), with Write a new post and replies only
 *     for whoever can post (canPost); Notifications for a member of an
 *     active group; the Season chart and Who is in this group for everyone;
 *   - a lead who runs her own group's list (and is not staff, who get Manage
 *     group instead of Families in your group) also reads "You lead this group";
 *   - Admin, Managers and Directors also read the staff steps;
 *   - a dancer reading her group gets her own three steps instead.
 * The page decides those from the same flags it gates the controls with, so
 * a Jr Staff reads the same steps on either sign-in.
 */
import { useId, useState, type CSSProperties, type ReactNode } from 'react';

export type BagGroupHowToVariant = 'member' | 'dancer';

const shellStyle: CSSProperties = {
  width: '100%',
  background: 'var(--lime-whisper)',
  border: '1px solid var(--lime-border)',
  borderRadius: 'var(--radius-lg, 16px)',
  padding: '0 var(--space-4, 16px)',
  display: 'flex',
  flexDirection: 'column',
  color: 'var(--ink)',
  minWidth: 0,
};

const headStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  width: '100%',
  minHeight: 48,
  padding: 0,
  border: 0,
  background: 'transparent',
  color: 'var(--ink)',
  font: 'inherit',
  fontSize: 15,
  fontWeight: 800,
  textAlign: 'left',
  cursor: 'pointer',
};

const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: 8,
  border: '1px solid var(--lime-border)',
  background: 'var(--white)',
  color: 'var(--lime-deep)',
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

const listStyle: CSSProperties = {
  margin: 0,
  padding: '0 0 0 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const stepStyle: CSSProperties = {
  fontSize: 16,
  fontWeight: 500,
  lineHeight: 1.5,
  color: 'var(--ink)',
};

const groupHeadStyle: CSSProperties = {
  margin: '6px 0 0',
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--lime-deep)',
};

function Chevron({ up }: { up: boolean }) {
  return (
    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
      <path d={up ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6'} />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--lime-deep)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}

/* The copy, word for word from RETHINK.md "How to use this page". */

const FAMILY_STEPS: ReactNode[] = [
  <><b>Your family</b> shows what your family is bringing, which competition it is for and the drop-off date. Either parent of your dancer sees the same list.</>,
  <>To sign up for something, look under <b>Still needs someone</b> and tap <b>I will bring this</b>. It moves to your list once it saves, and it shows on your To-dos too.</>,
  <>After you drop an item at the studio, tap <b>I dropped it off</b> and confirm. It reads Dropped off, waiting to be checked in, until your lead or the studio marks it received.</>,
  <><b>Posts</b> is where the group talks. Tap Write a new post, or the replies button under a post to answer it. React to a post the way you do on a class post. Done with this moves a post to Past for you only.</>,
  <>The <b>Notifications</b> button under the group name sets what this group sends you: All notifications, Just from the lead, or No notifications. Just from the lead keeps posts from your lead and from the studio. Phone and email alerts also follow Messages &amp; to-dos in Account.</>,
  <><b>Season chart</b> shows every family and every competition at a glance. <b>Who is in this group</b> lists the dancers and their parents.</>,
];

/* Step 4 for someone who can read the posts but not write or reply. */
const READ_ONLY_POSTS_STEP: ReactNode = <><b>Posts</b> is where the group talks.</>;

const LEAD_STEPS: ReactNode[] = [
  <><b>Run the list</b> is yours: <b>+ Add item</b> adds one, <b>Edit item</b> hands it to a family under Who brings it or opens it back up, <b>Mark received</b> checks in a drop-off, <b>Remove</b> takes it off the list.</>,
  <><b>Waiting to be checked in</b> at the top of Run the list shows what families have dropped off. Tap Mark received.</>,
  <><b>Families in your group</b> adds and removes dancers. Every parent linked to a dancer joins with her and is told once. You cannot take another lead or a staff member out this way; ask the studio.</>,
];

const STAFF_STEPS: ReactNode[] = [
  <><b>Manage group</b> renames the group, changes its season, sets the leads, adds parents by name or a staff lead, and archives or reactivates it. Adding a dancer adds every parent linked to her, now and later.</>,
  <>You can run any group&apos;s list. Directors can post in any active group; Admin and Managers post only in a group they are a member of.</>,
];

const DANCER_STEPS: ReactNode[] = [
  <><b>Your family is bringing</b> shows the items your parents signed up for, the competition and the drop-off date.</>,
  <><b>Posts</b> shows the newest 30 posts and their replies, with how many reacted and saw each one.</>,
  <>You can read this group. Posting, signing up and dropping off stay with your parents and the studio.</>,
];

export function BagGroupHowTo({
  variant = 'member',
  showLeadSteps = false,
  showStaffSteps = false,
  isMember = true,
  canAct = true,
  canReadPosts = true,
  canPost = true,
  showNotifications = true,
}: {
  variant?: BagGroupHowToVariant;
  /** A lead who runs her own group's list from Families in your group. */
  showLeadSteps?: boolean;
  /** Admin, Managers and Directors. */
  showStaffSteps?: boolean;
  /** In the group: Your family renders. */
  isMember?: boolean;
  /** I will bring this and I dropped it off render (the page's canActOnItems). */
  canAct?: boolean;
  /** The posts render rather than the private-wall line (the page's canReadChat). */
  canReadPosts?: boolean;
  /** Write a new post and replies render (the page's canPost). */
  canPost?: boolean;
  /** The Notifications button renders: a member of an active group. */
  showNotifications?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const dancer = variant === 'dancer';
  const lead = !dancer && showLeadSteps;
  const staff = !dancer && showStaffSteps;
  const familySteps: ReactNode[] = [
    ...(isMember ? [FAMILY_STEPS[0]] : []),
    ...(canAct ? [FAMILY_STEPS[1], FAMILY_STEPS[2]] : []),
    ...(canReadPosts ? [canPost ? FAMILY_STEPS[3] : READ_ONLY_POSTS_STEP] : []),
    ...(showNotifications ? [FAMILY_STEPS[4]] : []),
    FAMILY_STEPS[5],
  ];
  const base = dancer ? DANCER_STEPS : familySteps;
  const extrasLabel = staff ? 'Staff steps inside' : lead ? 'Lead steps inside' : null;
  const renderSteps = (steps: ReactNode[], start: number) => (
    <ol start={start} style={listStyle}>
      {steps.map((step, index) => (
        <li key={index} style={stepStyle}>{step}</li>
      ))}
    </ol>
  );
  return (
    <section aria-label="How to use this page" data-bag-howto={variant} style={shellStyle}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((value) => !value)}
        style={headStyle}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <InfoIcon />
          How to use this page
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {!open && extrasLabel && <span style={chipStyle}>{extrasLabel}</span>}
          <Chevron up={open} />
        </span>
      </button>
      {open && (
        <div id={panelId} style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 16 }}>
          {renderSteps(base, 1)}
          {lead && (
            <>
              <h3 style={groupHeadStyle}>You lead this group</h3>
              {renderSteps(LEAD_STEPS, base.length + 1)}
            </>
          )}
          {staff && (
            <>
              <h3 style={groupHeadStyle}>For Admin, Managers and Directors</h3>
              {renderSteps(STAFF_STEPS, base.length + (lead ? LEAD_STEPS.length : 0) + 1)}
            </>
          )}
        </div>
      )}
    </section>
  );
}

export default BagGroupHowTo;
